import { useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';

import { API_CACHE_KEYS } from '@/features/api/constants/cache';
import { useGrapevineChatApi } from '@/features/api/hooks/grapevine-chat/useGrapevineChatApi';
import { useAuth } from '@/features/authorization/provider/useAuth';
import { BadgeKey, useBadgeStore } from '@/shared/store/useBadgeStore';

const CACHE_KEY = [API_CACHE_KEYS.OWN_CHATS] as const;

export const useRefetchOwnChats = () => {
  const queryClient = useQueryClient();
  const { isAuthenticated } = useAuth();

  const api = useGrapevineChatApi();

  const updateBadge = useBadgeStore((state) => state.updateBadge);

  const refetchOwnChats = useCallback(async () => {
    if (!isAuthenticated) return [];

    //
    /**
     * Always fetch explicity, ensures that we get the current data. Needed for:
     * Case 1: In cache but there are no observers e.g. when the screen using the query is closed/inactive -> refetch even when inactive
     * Case 2: Not in cache but query was called once: fetch once
     * Case 3: Query was never called
     */
    const ownChats = await queryClient.fetchQuery({
      queryKey: CACHE_KEY,
      queryFn: () => api.getOwnChats(),
      staleTime: 0
    });

    const unreadMessageCount = ownChats.reduce(
      (sum, chat) => sum + (chat.unreadMessageCount ?? 0),
      0
    );

    updateBadge(
      BadgeKey.Chat,
      unreadMessageCount === 0 ? undefined : unreadMessageCount
    );

    return ownChats;
  }, [api, isAuthenticated, queryClient, updateBadge]);

  return { refetchOwnChats };
};
